import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, RoleOrgChart, SectionCard, StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSchoolRoleCreateValues,
  buildSchoolRoleFields,
  buildSchoolRoleOptions,
  buildSchoolRoleStats,
  buildSchoolRoleUpdateValues,
} from "pages/app/workspace/sections/forms/school-admin-section-forms";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { workspaceApi } from "pages/app/workspace/services";
import { hasAnyPortalAction } from "utils/portal-access";

export const SchoolRolesSection: React.FC<{ embedded?: boolean; portalAccess: any }> = ({
  embedded = false,
  portalAccess,
}) => {
  const { schoolId, selectedSchool } = useScopedSchoolSelection({
    portalAccess,
  });

  const rolesQuery = useQuery({
    queryKey: ["workspace-school-roles", schoolId],
    queryFn: () => workspaceApi.schoolRoles.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const canCreateRoles = hasAnyPortalAction(portalAccess, [
    "school_role_assignments.manage",
    "school_role_assignments.assign_head_of_sports",
    "school_role_assignments.assign_head_of_sport",
    "school_role_assignments.assign_coach",
  ]);
  const canEditRoles = hasAnyPortalAction(portalAccess, [
    "school_role_assignments.manage",
    "school_role_assignments.update_head_of_sports",
    "school_role_assignments.assign_head_of_sports",
    "school_role_assignments.assign_head_of_sport",
    "school_role_assignments.assign_coach",
  ]);
  const canRemoveRoles = hasAnyPortalAction(portalAccess, [
    "school_role_assignments.delete",
    "school_role_assignments.manage",
  ]);
  const usersQuery = useQuery({
    queryKey: ["workspace-school-role-users", schoolId],
    queryFn: () => workspaceApi.schoolRoles.listCandidates(schoolId),
    enabled: Boolean(schoolId),
  });
  const schoolSportsQuery = useQuery({
    queryKey: ["workspace-school-role-sports", schoolId],
    queryFn: () => workspaceApi.schoolSports.list(schoolId),
    enabled: Boolean(schoolId),
  });

  const roleOptions = buildSchoolRoleOptions(portalAccess);
  const roleFields = buildSchoolRoleFields({
    assignments: rolesQuery.data || [],
    roleOptions,
    schoolSports: schoolSportsQuery.data || [],
    users: usersQuery.data || [],
  });
  const roleStats = useMemo(() => buildSchoolRoleStats(rolesQuery.data || []), [rolesQuery.data]);

  // TODO(staff-creation): How are staff users created/invited? This portal only assigns
  // school roles to existing candidates (`listCandidates` + POST .../roles). There is no
  // create-staff / invite flow yet — defer until product/API clarifies People → Staff IA.
  return (
    <CrudSection
      buildCreateValues={buildSchoolRoleCreateValues}
      buildUpdateValues={buildSchoolRoleUpdateValues}
      canCreate={canCreateRoles && Boolean(schoolId)}
      canEdit={canEditRoles && Boolean(schoolId)}
      canRemove={canRemoveRoles && Boolean(schoolId)}
      columns={[
        { label: "User", accessor: (row) => row.user_name || "Unknown" },
        { label: "Role", accessor: (row) => <StatusPill value={row.role} /> },
        { label: "Sport", accessor: (row) => row.sport_name || "All school sports" },
        {
          label: "Active",
          accessor: (row) => <StatusPill value={row.active ? "active" : "inactive"} />,
        },
      ]}
      createLabel="Assign role"
      data={rolesQuery.data || []}
      description={
        canCreateRoles || canEditRoles || canRemoveRoles
          ? "Manage school role assignments for the selected school with scoped user candidates, sport-aware role selection, and a live organisational view."
          : "Review school role assignments for the selected school."
      }
      fields={roleFields}
      helperCardPlacement="below"
      helperCard={
        schoolId ? (
          <SectionCard
            subtitle="Optional visual aid for reporting lines — expand when you need the hierarchy."
            title="Organisation Structure"
          >
            <RoleOrgChart
              assignments={rolesQuery.data || []}
              schoolName={selectedSchool?.name}
              schoolId={schoolId}
              users={usersQuery.data || []}
            />
          </SectionCard>
        ) : (
          <SectionCard
            subtitle="Choose a school to visualise reporting lines and active assignments."
            title="Organisation Structure"
          >
            <EmptyState
              description="The org chart will appear here once a school is selected."
              title="No school selected"
            />
          </SectionCard>
        )
      }
      invalidateKeys={[
        ["workspace-school-roles", schoolId],
        ["workspace-school-role-users", schoolId],
      ]}
      isLoading={rolesQuery.isLoading}
      onCreate={(payload) => workspaceApi.schoolRoles.create(schoolId, payload)}
      onRemove={(row) => workspaceApi.schoolRoles.remove(schoolId, row.id)}
      onUpdate={(row, payload) => workspaceApi.schoolRoles.update(schoolId, row.id, payload)}
      removeSuccessMessage="Role assignment removed."
      stats={roleStats}
      embedded={embedded}
      showTitle={!embedded}
      title="School Roles"
    />
  );
};
